import { Link } from "react-router-dom";
import { ArrowLeft, Clock, ShieldCheck, FileText, Users, CreditCard, Scale, AlertTriangle, RefreshCw, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const TermosDeUso = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="h-4 w-4" /> Voltar ao início
        </Link>

        <div className="mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium mb-4">
            Documento Legal
          </span>
          <h1 className="text-4xl font-bold mb-2">Termos e Condições de Uso</h1>
          <p className="text-muted-foreground">Última atualização: 09 de março de 2026</p>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <Clock className="h-4 w-4" />
            <span>Tempo estimado de leitura: 15 minutos</span>
          </div>
        </div>

        {/* Intro */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <p className="text-muted-foreground leading-relaxed">
            Olá! Bem-vindo(a) ao <strong className="text-foreground">ViaMenu</strong>. Por meio deste documento, estabelecemos os termos e condições gerais para a utilização da Plataforma ViaMenu ("Plataforma"), por Você ("Você" ou "Usuário"), que é nosso cliente — seja como proprietário de restaurante, funcionário autorizado ou consumidor final.
          </p>
          <p className="text-muted-foreground leading-relaxed mt-3">
            Nosso propósito é revolucionar a gestão de restaurantes no Brasil, oferecendo ferramentas digitais completas para que estabelecimentos de todos os tamanhos possam crescer e atender melhor seus clientes.
          </p>
        </div>

        {/* Highlights */}
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <ShieldCheck className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">#SuaResponsabilidade</p>
            <p className="text-xs text-muted-foreground mt-1">Mantenha suas credenciais seguras e use a plataforma com ética.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <Scale className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">#SeuDireito</p>
            <p className="text-xs text-muted-foreground mt-1">Seus dados e conteúdo permanecem seus. Cancele a qualquer momento.</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 text-center">
            <FileText className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-xs font-medium">#NossoCompromisso</p>
            <p className="text-xs text-muted-foreground mt-1">Transparência, segurança e suporte para o seu negócio crescer.</p>
          </div>
        </div>

        {/* Sections */}
        <Accordion type="multiple" defaultValue={["1"]} className="space-y-3">
          <AccordionItem value="1" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><FileText className="h-5 w-5 text-primary" /> 1. O que é o ViaMenu?</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                O <strong className="text-foreground">ViaMenu</strong> é uma plataforma SaaS (Software as a Service) que oferece um ecossistema completo de ferramentas digitais para restaurantes, bares, lanchonetes, cafeterias e estabelecimentos do setor alimentício. A Plataforma conecta:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Restaurantes</strong> — proprietários e funcionários que utilizam as ferramentas de gestão (cardápio digital, PDV, painel da cozinha, delivery, estoque, financeiro, marketing e relatórios);</li>
                <li><strong className="text-foreground">Consumidores finais</strong> — clientes dos restaurantes que acessam o cardápio digital, fazem pedidos, acompanham entregas e participam de programas de fidelidade;</li>
                <li><strong className="text-foreground">Entregadores</strong> — profissionais cadastrados pelos restaurantes para realizar entregas de pedidos delivery.</li>
              </ul>
              <div className="bg-muted rounded-lg p-4 mt-3">
                <p className="text-sm">
                  <strong className="text-foreground">Importante:</strong> O ViaMenu é uma plataforma tecnológica. Não somos produtores, fornecedores ou transportadores de alimentos. Os restaurantes cadastrados são os únicos responsáveis pela qualidade, preparo, acondicionamento e entrega dos produtos comercializados por meio da Plataforma.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="2" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Users className="h-5 w-5 text-primary" /> 2. Quem pode usar o ViaMenu?</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Os serviços da Plataforma ViaMenu estão disponíveis para:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Restaurantes:</strong> Pessoas jurídicas ou físicas maiores de 18 anos, com CNPJ ou CPF válido, que desejam utilizar as ferramentas de gestão;</li>
                <li><strong className="text-foreground">Consumidores:</strong> Pessoas físicas maiores de 16 anos que acessam os cardápios digitais e fazem pedidos;</li>
                <li><strong className="text-foreground">Funcionários:</strong> Pessoas autorizadas pelo restaurante para operar funcionalidades específicas (caixa, cozinha, etc.) mediante credenciais fornecidas pelo estabelecimento.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">2.1. Sobre sua Conta</h4>
              <p>
                Ao criar uma conta no ViaMenu, Você se compromete a fornecer informações verdadeiras, atualizadas e completas. Cada usuário pode manter apenas <strong className="text-foreground">1 (uma) conta ativa</strong>. A duplicidade de contas poderá resultar no cancelamento sem aviso prévio.
              </p>

              <h4 className="font-semibold text-foreground mt-4">2.2. Suas responsabilidades</h4>
              <ul className="list-decimal pl-5 space-y-1">
                <li>Manter a confidencialidade e segurança de suas credenciais de acesso — são pessoais e intransferíveis;</li>
                <li>Responder por todas as atividades realizadas em sua conta;</li>
                <li>Fornecer informações corretas e atualizadas sobre o estabelecimento;</li>
                <li>Manter comunicação respeitosa com consumidores, entregadores e com o suporte do ViaMenu;</li>
                <li>Cumprir todas as leis aplicáveis, incluindo legislação sanitária, tributária e de proteção ao consumidor;</li>
                <li>Garantir que os produtos listados no cardápio digital estejam disponíveis e com preços corretos.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">2.3. Suspensão e cancelamento</h4>
              <p>O ViaMenu poderá suspender ou cancelar sua conta nos seguintes casos:</p>
              <ul className="list-roman pl-5 space-y-1">
                <li>Descumprimento destes Termos ou do Código de Conduta;</li>
                <li>Detecção de fraude, anomalia ou uso indevido da Plataforma;</li>
                <li>Informações falsas ou inverídicas no cadastro;</li>
                <li>Inadimplência no pagamento do plano contratado por mais de 30 dias;</li>
                <li>Inatividade da conta por mais de 12 meses consecutivos;</li>
                <li>Determinação judicial ou descumprimento de leis aplicáveis.</li>
              </ul>

              <div className="bg-muted rounded-lg p-4 mt-3">
                <p className="text-sm">
                  O ViaMenu <strong className="text-foreground">não tolera</strong> qualquer forma de preconceito, assédio, discriminação ou incitação à violência. Condutas desta natureza resultarão no cancelamento imediato da conta, sem prejuízo de medidas judiciais cabíveis.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="3" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><CreditCard className="h-5 w-5 text-primary" /> 3. Planos, Pagamentos e Assinaturas</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <h4 className="font-semibold text-foreground">3.1. Planos disponíveis</h4>
              <p>
                O ViaMenu oferece diferentes planos de assinatura, incluindo opções gratuitas e pagas, com funcionalidades e limites distintos. Os planos, preços e condições estão detalhados na <Link to="/#precos" className="text-primary hover:underline">página de preços</Link> e podem ser alterados mediante aviso prévio de 30 dias.
              </p>

              <h4 className="font-semibold text-foreground mt-4">3.2. Período de teste (Trial)</h4>
              <p>
                Alguns planos oferecem período de teste gratuito. Ao término do trial, caso não haja cancelamento, o plano será automaticamente convertido para a modalidade paga. Você será notificado antes do término do período de teste.
              </p>

              <h4 className="font-semibold text-foreground mt-4">3.3. Cobrança e pagamento</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Os planos pagos são cobrados de forma recorrente (mensal ou anual), conforme a opção escolhida;</li>
                <li>O processamento de pagamentos é realizado pelo <strong className="text-foreground">Mercado Pago</strong>, que atua como processador de pagamentos autorizado;</li>
                <li>Aceitamos cartão de crédito e PIX como formas de pagamento;</li>
                <li>Os dados completos de cartões <strong className="text-foreground">não são armazenados</strong> em nossos servidores — são processados exclusivamente pelo Mercado Pago com os mais altos padrões de segurança (PCI DSS).</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">3.4. Cancelamento e reembolso</h4>
              <p>
                Você pode cancelar sua assinatura a qualquer momento pelo painel de configurações. O cancelamento terá efeito ao final do período vigente já pago. Não há reembolso proporcional para períodos parcialmente utilizados, exceto nos primeiros 7 dias da primeira contratação (direito de arrependimento conforme o Código de Defesa do Consumidor).
              </p>

              <h4 className="font-semibold text-foreground mt-4">3.5. Inadimplência</h4>
              <p>
                Em caso de não pagamento, o ViaMenu poderá: (i) restringir funcionalidades do plano; (ii) suspender o acesso após 15 dias; (iii) cancelar a conta após 60 dias. Seus dados serão mantidos por 90 dias após o cancelamento por inadimplência.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="4" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-primary" /> 4. Uso Aceitável da Plataforma</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Ao utilizar o ViaMenu, Você se compromete a <strong className="text-foreground">NÃO</strong>:</p>
              <ul className="list-decimal pl-5 space-y-1">
                <li>Utilizar a Plataforma para fins ilegais ou não autorizados;</li>
                <li>Acessar, copiar, modificar ou distribuir qualquer parte do código-fonte da Plataforma;</li>
                <li>Tentar acessar sistemas, contas ou dados sem autorização;</li>
                <li>Transmitir vírus, malware ou qualquer código malicioso;</li>
                <li>Sobrecarregar intencionalmente a infraestrutura (ataques DDoS, scraping abusivo, etc.);</li>
                <li>Cadastrar produtos falsos, com preços enganosos ou informações incorretas;</li>
                <li>Utilizar a Plataforma para comercializar produtos ilegais, controlados ou que violem regulamentações sanitárias;</li>
                <li>Praticar concorrência desleal, spam ou qualquer forma de abuso contra outros usuários;</li>
                <li>Sublicenciar, revender ou redistribuir o acesso à Plataforma sem autorização expressa.</li>
              </ul>

              <div className="bg-muted rounded-lg p-4 mt-3">
                <p className="text-sm">
                  O ViaMenu concede a Você uma <strong className="text-foreground">licença não exclusiva, intransferível e não sublicenciável</strong> para utilizar a Plataforma durante a vigência da sua assinatura, exclusivamente para os fins descritos nestes Termos.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="5" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><FileText className="h-5 w-5 text-primary" /> 5. Propriedade Intelectual</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <h4 className="font-semibold text-foreground">5.1. Propriedade do ViaMenu</h4>
              <p>
                Todo o conteúdo, design, logotipos, marcas, código-fonte, funcionalidades, algoritmos e documentação da Plataforma ViaMenu são de propriedade exclusiva do ViaMenu e estão protegidos pela legislação brasileira de propriedade intelectual (Lei nº 9.610/98 e Lei nº 9.279/96).
              </p>

              <h4 className="font-semibold text-foreground mt-4">5.2. Seus dados e conteúdo</h4>
              <p>
                Você mantém a <strong className="text-foreground">propriedade integral</strong> de todos os dados, imagens, textos e informações que insere na Plataforma (cardápio, produtos, logotipos, fotos, etc.). Ao utilizar a Plataforma, Você nos concede uma licença limitada, não exclusiva e revogável para processar, armazenar e exibir esse conteúdo exclusivamente para a prestação do Serviço.
              </p>

              <h4 className="font-semibold text-foreground mt-4">5.3. Exportação de dados</h4>
              <p>
                Você tem o direito de exportar seus dados a qualquer momento. Em caso de cancelamento, disponibilizaremos seus dados para exportação por 90 dias.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="6" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><AlertTriangle className="h-5 w-5 text-primary" /> 6. Limitação de Responsabilidade</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <h4 className="font-semibold text-foreground">6.1. Disponibilidade do serviço</h4>
              <p>
                O ViaMenu é fornecido "no estado em que se encontra" (as is). Embora nos esforcemos para manter a Plataforma disponível 24/7, não garantimos que o serviço será ininterrupto ou livre de erros. A Plataforma poderá ficar temporariamente indisponível devido a:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Manutenções programadas (com aviso prévio de 24h) ou emergenciais;</li>
                <li>Falhas em provedores de infraestrutura terceirizados;</li>
                <li>Caso fortuito ou força maior.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">6.2. Limitação de responsabilidade</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>O ViaMenu <strong className="text-foreground">não é responsável</strong> pela qualidade, segurança alimentar ou entrega dos produtos comercializados pelos restaurantes;</li>
                <li>O ViaMenu <strong className="text-foreground">não é responsável</strong> por perdas de receita decorrentes de indisponibilidade temporária da Plataforma;</li>
                <li>Nossa responsabilidade total está limitada ao valor pago pelo Usuário nos últimos 12 meses de assinatura;</li>
                <li>Não nos responsabilizamos por danos indiretos, incidentais, especiais ou consequenciais.</li>
              </ul>

              <h4 className="font-semibold text-foreground mt-4">6.3. Integrações de terceiros</h4>
              <p>
                A Plataforma oferece integrações com serviços de terceiros (iFood, Mercado Pago, etc.). O ViaMenu não se responsabiliza por falhas, indisponibilidades ou alterações nos serviços desses terceiros. Cada integração está sujeita aos termos de uso do respectivo serviço.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="7" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><RefreshCw className="h-5 w-5 text-primary" /> 7. Alterações nos Termos</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>
                O ViaMenu reserva-se o direito de modificar estes Termos a qualquer momento, observando as seguintes regras:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Alterações significativas:</strong> Notificação por e-mail e banner na Plataforma com antecedência mínima de 30 dias;</li>
                <li><strong className="text-foreground">Alterações menores:</strong> Atualização na data de publicação deste documento;</li>
                <li>O uso continuado da Plataforma após o período de notificação constitui aceitação dos novos Termos;</li>
                <li>Caso não concorde com as alterações, Você poderá cancelar sua conta sem ônus antes da entrada em vigor dos novos Termos.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="8" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Scale className="h-5 w-5 text-primary" /> 8. Disposições Gerais</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <h4 className="font-semibold text-foreground">8.1. Legislação aplicável</h4>
              <p>
                Estes Termos são regidos pela legislação da República Federativa do Brasil, em especial pelo Código Civil, Código de Defesa do Consumidor (Lei nº 8.078/90), Marco Civil da Internet (Lei nº 12.965/14) e Lei Geral de Proteção de Dados (Lei nº 13.709/18).
              </p>

              <h4 className="font-semibold text-foreground mt-4">8.2. Foro</h4>
              <p>
                Fica eleito o foro da comarca de São Paulo/SP para dirimir quaisquer controvérsias decorrentes destes Termos, com renúncia a qualquer outro, por mais privilegiado que seja.
              </p>

              <h4 className="font-semibold text-foreground mt-4">8.3. Independência das cláusulas</h4>
              <p>
                Caso qualquer disposição destes Termos seja considerada inválida ou inexequível, as demais disposições permanecerão em pleno vigor e efeito.
              </p>

              <h4 className="font-semibold text-foreground mt-4">8.4. Cessão</h4>
              <p>
                Você não poderá ceder ou transferir seus direitos e obrigações previstos nestes Termos sem o consentimento prévio do ViaMenu. O ViaMenu poderá ceder livremente este contrato no contexto de reorganizações societárias.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="9" className="bg-card border border-border rounded-xl px-6">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline">
              <span className="flex items-center gap-3"><Mail className="h-5 w-5 text-primary" /> 9. Contato</span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed space-y-3 pb-6">
              <p>Para dúvidas, sugestões ou reclamações sobre estes Termos:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">E-mail:</strong> <a href="mailto:contato@viamenu.com.br" className="text-primary hover:underline">contato@viamenu.com.br</a></li>
                <li><strong className="text-foreground">Central de Ajuda:</strong> <Link to="/ajuda" className="text-primary hover:underline">viamenu.com.br/ajuda</Link></li>
                <li><strong className="text-foreground">Página de Contato:</strong> <Link to="/contato" className="text-primary hover:underline">viamenu.com.br/contato</Link></li>
              </ul>
              <p className="mt-3">
                O ViaMenu se coloca à disposição para auxiliar no que for necessário. Conte conosco! 🧡
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};

export default TermosDeUso;
